import { ModalV2, Prompt } from '@root/components'
import { useBusinessContext } from '@root/context'
import { CancelSubscriptionProps, HttpError, useChurnReasons, useStripeAddons } from '@root/query'
import { APIError, setAPIErrors } from '@utils'
import React, { useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CancelFlowFooter } from './CancelFlowFooter'
import { Content, ScrollContent } from './CancelFlow.styled'
import { CancelOptionsStep, ChurnReasonStep } from './steps'
import {
  cancelFlowDefaultValues,
  CancelFlowFormValues,
  CancelFlowStep,
  churnReasonSelectionNeedsText,
  mapCancelFlowApiErrors
} from './types'

const cancelFlowStepContent = (
  activeAddonsCount: number
): Record<CancelFlowStep, React.ReactNode> => ({
  'cancellation-options': <CancelOptionsStep activeAddonsCount={activeAddonsCount} />,
  'churn-reason': <ChurnReasonStep />
})

interface Props {
  isVisible: boolean
  isSubmitting: boolean
  onClose: () => void
  onConfirm: (props: CancelSubscriptionProps) => Promise<void>
}

export const CancelFlow: React.FC<Props> = ({
  isVisible,
  isSubmitting,
  onClose: _onClose,
  onConfirm
}) => {
  const { t } = useTranslation()
  const { slug, data } = useBusinessContext()
  const { data: churnReasons } = useChurnReasons(slug, true)
  const { data: addonsData } = useStripeAddons({
    slug,
    enabled: true
  })
  const activeAddons = useMemo(
    () => addonsData?.addons.filter(addon => addon.active) ?? [],
    [addonsData?.addons]
  )
  const [promptVisible, setPromptVisible] = useState(false)
  const [currentStep, setCurrentStep] = useState<CancelFlowStep>('cancellation-options')

  const form = useForm<CancelFlowFormValues>({
    defaultValues: cancelFlowDefaultValues
  })
  const { control, reset, setError } = form

  const disableEinvoicing = useWatch({ control, name: 'disableEinvoicing' })
  const keepAddons = useWatch({ control, name: 'keepAddons' })
  const selectedReasons = useWatch({ control, name: 'churnReasons' }) ?? []
  const churnReasonOther = useWatch({ control, name: 'churnReasonOther' }) ?? ''

  const onClose = useCallback(() => {
    reset()
    setCurrentStep('cancellation-options')
    setPromptVisible(false)
    _onClose()
  }, [_onClose, reset])

  const reasonNeedsText = churnReasonSelectionNeedsText(selectedReasons, churnReasons)
  const isCancellationOptionsStepValid =
    (!data.einvoicing_enabled || disableEinvoicing !== undefined) &&
    (activeAddons.length === 0 || keepAddons !== undefined)
  const isChurnReasonStepValid =
    selectedReasons.length > 0 && (!reasonNeedsText || churnReasonOther.trim().length > 0)

  const handleBack = () => {
    if (currentStep === 'cancellation-options') {
      onClose()
      return
    }
    setCurrentStep('cancellation-options')
  }

  const handleConfirm = async () => {
    if (selectedReasons.length === 0) return
    try {
      await onConfirm({
        slug,
        disableEinvoicing: disableEinvoicing ?? false,
        keepAddons: keepAddons ?? false,
        churnReasons: selectedReasons,
        churnReasonOther: reasonNeedsText ? churnReasonOther.trim() : undefined
      })
    } catch (error) {
      const { status, data } = (error as HttpError<APIError>) ?? {}
      if (status === 400 && data) {
        setPromptVisible(false)
        setCurrentStep('churn-reason')
        setAPIErrors(mapCancelFlowApiErrors(data), setError)
        return
      }
      throw error
    }
  }

  return (
    <>
      <ModalV2
        isVisible={isVisible}
        handleOnClose={onClose}
        header={t('settings.billing.cancelSubscription')}
        width={600}
        height={800}
      >
        <FormProvider {...form}>
          <Content>
            <ScrollContent>{cancelFlowStepContent(activeAddons.length)[currentStep]}</ScrollContent>

            <CancelFlowFooter
              currentStep={currentStep}
              isCancellationOptionsStepValid={isCancellationOptionsStepValid}
              isChurnReasonStepValid={isChurnReasonStepValid}
              isSubmitting={isSubmitting}
              onBack={handleBack}
              onNext={() => setCurrentStep('churn-reason')}
              onCancelPlan={() => setPromptVisible(true)}
            />
          </Content>
        </FormProvider>
      </ModalV2>

      <Prompt
        isVisible={promptVisible}
        onClose={() => setPromptVisible(false)}
        title={t('settings.billing.cancelPromptTitle')}
        description={
          <>
            <p>{t('settings.billing.cancelPromptDescription')}</p>
          </>
        }
        buttons={[
          {
            text: t('settings.billing.keepSubscription'),
            action: () => {
              setPromptVisible(false)
              onClose()
            }
          },
          {
            text: t('settings.billing.cancelSubscription'),
            intent: 'danger',
            disabled: isSubmitting,
            showSpinner: isSubmitting,
            action: handleConfirm
          }
        ]}
      />
    </>
  )
}
